import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, formatDistanceToNow } from 'date-fns';
import { useData } from '../contexts/DataContext';
import SafeIcon from '../common/SafeIcon';
import NoteCard from '../components/NoteCard';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiMail, FiPhone, FiMapPin, FiCalendar, FiClock, FiEdit, FiPlus, FiArrowLeft, FiPackage, FiTarget, FiCheckSquare, FiTrash2, FiArchive } = FiIcons;

const MemberDetail = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { 
    members, 
    getMemberNotes, 
    loading, 
    archiveMember,
    getMemberGoals,
    getMemberMiniGoals,
    updateMemberGoal,
    addMemberGoal,
    addMemberMiniGoal,
    updateMemberMiniGoal
  } = useData();

  const [activeTab, setActiveTab] = useState('notes');
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [newGoal, setNewGoal] = useState('');
  const [goalType, setGoalType] = useState('main'); // 'main' or 'mini'

  const member = members.find(m => String(m.id) === String(memberId));
  const notes = getMemberNotes(memberId);
  const mainGoals = getMemberGoals(memberId).filter(g => !g.is_archived);
  const miniGoals = getMemberMiniGoals(memberId).filter(g => !g.is_archived);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="p-6 text-center">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md mx-auto">
          <SafeIcon icon={FiUser} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Member Not Found</h2>
          <p className="text-gray-600 mb-6">The member you are looking for does not exist or has been removed.</p>
          <Link
            to="/members"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
            Back to Members
          </Link>
        </div>
      </div>
    );
  }

  const handleAddGoal = async (e) => {
    e.preventDefault();
    if (!newGoal.trim()) return;

    if (goalType === 'main') {
      await addMemberGoal({
        member_id: memberId,
        description: newGoal,
        status: 'pending'
      });
    } else {
      await addMemberMiniGoal({
        member_id: memberId,
        description: newGoal,
        status: 'pending'
      });
    }

    setNewGoal('');
    setShowGoalModal(false);
  };

  const toggleGoalStatus = async (goal, type) => {
    const newStatus = goal.status === 'completed' ? 'pending' : 'completed';
    if (type === 'main') {
      await updateMemberGoal({ ...goal, status: newStatus });
    } else {
      await updateMemberMiniGoal({ ...goal, status: newStatus });
    }
  };

  return (
    <div className="p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <Link to="/members" className="inline-flex items-center text-gray-500 hover:text-gray-900 mb-4">
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-1" />
          Back to Members
        </Link>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4">
            <Link
              to={`/edit-member/${member.id}`}
              className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
              title="Edit Member"
            >
              <SafeIcon icon={FiEdit} className="w-5 h-5" />
            </Link>
          </div>

          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={member.is_archived ? FiArchive : FiUser} className="w-10 h-10 text-primary-600" />
            </div>

            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                {member.name}
                {member.is_archived && (
                  <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full font-normal">
                    Archived
                  </span>
                )}
              </h1>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                <div className="flex items-center text-gray-600">
                  <SafeIcon icon={FiMail} className="w-4 h-4 mr-2 text-gray-400" />
                  <a href={`mailto:${member.email}`} className="hover:text-primary-600 text-sm">{member.email}</a>
                </div>
                
                {member.phone && (
                  <div className="flex items-center text-gray-600">
                    <SafeIcon icon={FiPhone} className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="text-sm">{member.phone}</span>
                  </div>
                )}

                <div className="flex items-center text-gray-600">
                  <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="text-sm">
                    {[member.city, member.state, member.country].filter(Boolean).join(', ') || 'No location'}
                  </span>
                </div>

                <div className="flex items-center text-gray-600">
                  <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="text-sm">Joined: {format(new Date(member.date_joined), 'MMM d, yyyy')}</span>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {member.programs && member.programs.map(program => (
                  <span key={program} className="px-3 py-1 bg-primary-50 text-primary-700 text-xs rounded-full font-medium flex items-center">
                    <SafeIcon icon={FiPackage} className="w-3 h-3 mr-1" />
                    {program}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="flex space-x-4 border-b border-gray-200 mb-6 overflow-x-auto">
        <button
          onClick={() => setActiveTab('notes')}
          className={`pb-2 px-1 text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'notes' 
              ? 'border-b-2 border-primary-600 text-primary-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Session Notes ({notes.length})
        </button>
        <button
          onClick={() => setActiveTab('goals')}
          className={`pb-2 px-1 text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'goals' 
              ? 'border-b-2 border-primary-600 text-primary-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Goals & Progress
        </button>
      </div>

      {activeTab === 'notes' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Session History</h2>
            <Link
              to={`/add-note/${member.id}`}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              <span>Add Note</span>
            </Link>
          </div>

          <div className="space-y-4">
            {notes.length > 0 ? (
              notes.map(note => (
                <NoteCard key={note.id} note={note} />
              ))
            ) : (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <SafeIcon icon={FiClock} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No session notes recorded yet.</p>
                <Link
                  to={`/add-note/${member.id}`}
                  className="inline-flex items-center space-x-2 mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                >
                  <SafeIcon icon={FiPlus} className="w-4 h-4" />
                  <span>Add First Note</span>
                </Link>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {activeTab === 'goals' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {/* Main Goals Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <SafeIcon icon={FiTarget} className="w-5 h-5 mr-2 text-primary-600" />
                Main Goals
              </h3>
              <button
                onClick={() => {
                  setGoalType('main');
                  setShowGoalModal(true);
                }}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                + Add Goal
              </button>
            </div>
            
            <div className="space-y-3">
              {mainGoals.length > 0 ? (
                mainGoals.map(goal => (
                  <div 
                    key={goal.id} 
                    className={`flex items-start p-3 rounded-lg border ${goal.status === 'completed' ? 'bg-green-50 border-green-100' : 'bg-gray-50 border-gray-100'}`}
                  >
                    <button
                      onClick={() => toggleGoalStatus(goal, 'main')}
                      className={`mt-0.5 mr-3 flex-shrink-0 ${goal.status === 'completed' ? 'text-green-500' : 'text-gray-400 hover:text-primary-500'}`}
                    >
                      <SafeIcon icon={FiCheckSquare} className="w-5 h-5" />
                    </button>
                    <span className={`text-sm flex-1 ${goal.status === 'completed' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                      {goal.description}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No main goals set yet.</p>
              )}
            </div>
          </div>

          {/* Mini Goals Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <SafeIcon icon={FiCheckSquare} className="w-5 h-5 mr-2 text-green-600" />
                Mini Goals / Action Items
              </h3>
              <button
                onClick={() => {
                  setGoalType('mini');
                  setShowGoalModal(true);
                }}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                + Add Item
              </button>
            </div>
            
            <div className="space-y-3">
              {miniGoals.length > 0 ? (
                miniGoals.map(goal => (
                  <div 
                    key={goal.id} 
                    className={`flex items-start p-3 rounded-lg border ${goal.status === 'completed' ? 'bg-green-50 border-green-100' : 'bg-gray-50 border-gray-100'}`}
                  >
                    <button
                      onClick={() => toggleGoalStatus(goal, 'mini')}
                      className={`mt-0.5 mr-3 flex-shrink-0 ${goal.status === 'completed' ? 'text-green-500' : 'text-gray-400 hover:text-primary-500'}`}
                    >
                      <SafeIcon icon={FiCheckSquare} className="w-5 h-5" />
                    </button>
                    <span className={`text-sm flex-1 ${goal.status === 'completed' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                      {goal.description}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No mini goals set yet.</p>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Add Goal Modal */}
      {showGoalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Add {goalType === 'main' ? 'Main Goal' : 'Mini Goal'}
            </h3>
            <form onSubmit={handleAddGoal}>
              <input
                type="text"
                value={newGoal}
                onChange={(e) => setNewGoal(e.target.value)}
                placeholder={`Enter ${goalType === 'main' ? 'high-level goal' : 'actionable task'}...`}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-4"
                autoFocus
              />
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowGoalModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newGoal.trim()}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  Add Goal
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default MemberDetail;